import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Phone, ChevronDown, Menu, MapPin, Mail, BadgeCheck } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { SITE, NAV, COUNTIES } from '../../data/site';

const NAV_LINK_CLASS = 'font-heading font-semibold text-[15px] text-foreground/75 hover:text-primary transition-colors duration-200';
const MOBILE_LINK_CLASS = 'text-left px-2 py-2 font-heading font-medium text-foreground/80 hover:text-primary';

const LOCATION_ITEMS = COUNTIES.map((c) => ({ label: c.name, path: `/locations/new-jersey/${c.slug}` }));

const SIMPLE_LINKS = [
  { label: 'Gutters', path: '/gutters', testId: 'nav-gutters' },
  { label: 'Siding', path: '/siding', testId: 'nav-siding' },
  { label: 'Our Work', path: '/our-work', testId: 'nav-our-work' },
];

const TRAILING_LINKS = [
  { label: 'Contact', path: '/contact-us', testId: 'nav-contact' },
  { label: 'Blog', path: '/blog', testId: 'nav-blog' },
];

const TopBar = () => (
  <div className="hidden md:block bg-dark text-dark-foreground/90">
    <div className="max-w-[1320px] mx-auto px-4 lg:px-8 h-10 flex items-center justify-between text-[13px] font-body">
      <div className="flex items-center gap-6">
        <a href={SITE.addressMapHref} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 hover:text-primary-foreground transition-colors">
          <MapPin className="w-3.5 h-3.5 text-[hsl(var(--primary-glow))]" /> {SITE.address}
        </a>
        <a href={`mailto:${SITE.email}`} className="hidden lg:flex items-center gap-1.5 hover:text-primary-foreground transition-colors">
          <Mail className="w-3.5 h-3.5 text-[hsl(var(--primary-glow))]" /> {SITE.email}
        </a>
      </div>
      <p className="flex items-center gap-1.5">
        <BadgeCheck className="w-3.5 h-3.5 text-[hsl(var(--primary-glow))]" />
        Free Inspections – Same Day Service
      </p>
    </div>
  </div>
);

const Dropdown = ({ label, items }) => (
  <div className="relative group">
    <button className={`flex items-center gap-1 ${NAV_LINK_CLASS} py-6`} data-testid={`nav-${label.toLowerCase()}`}>
      {label}
      <ChevronDown className="w-4 h-4 mt-0.5 transition-transform duration-200 group-hover:rotate-180" />
    </button>
    <div className="absolute left-1/2 -translate-x-1/2 top-full z-50 hidden group-hover:block min-w-[240px] pt-1">
      <div className="bg-background rounded-2xl shadow-card border border-border overflow-hidden p-2">
        {items.map((item) => (
          <Link
            key={item.label}
            to={item.path}
            className="block px-4 py-2.5 rounded-xl font-heading font-medium text-[14px] text-foreground/75 hover:bg-primary/10 hover:text-primary transition-colors duration-200"
          >
            {item.label}
          </Link>
        ))}
      </div>
    </div>
  </div>
);

const DesktopNav = () => (
  <nav className="hidden lg:flex items-center gap-6">
    <Dropdown label="Roofing" items={NAV.roofing} />
    <Dropdown label="Chimney" items={NAV.chimney} />
    {SIMPLE_LINKS.map((l) => (
      <Link key={l.label} to={l.path} className={NAV_LINK_CLASS} data-testid={l.testId}>{l.label}</Link>
    ))}
    <Dropdown label="Locations" items={LOCATION_ITEMS} />
    {TRAILING_LINKS.map((l) => (
      <Link key={l.label} to={l.path} className={NAV_LINK_CLASS} data-testid={l.testId}>{l.label}</Link>
    ))}
  </nav>
);

const MobileMenuGroup = ({ title, items, onGo }) => (
  <>
    <p className="font-heading font-bold uppercase text-[11px] text-muted-foreground tracking-widest px-2 mb-1">{title}</p>
    {items.map((i) => (
      <button key={i.label} onClick={() => onGo(i.path)} className={MOBILE_LINK_CLASS}>{i.label}</button>
    ))}
  </>
);

const MobileMenu = ({ open, onOpenChange, onGo }) => (
  <Sheet open={open} onOpenChange={onOpenChange}>
    <SheetTrigger asChild>
      <button className="lg:hidden p-2 text-foreground" aria-label="Open menu" data-testid="mobile-menu-button">
        <Menu className="w-7 h-7" />
      </button>
    </SheetTrigger>
    <SheetContent side="right" className="w-[310px] overflow-y-auto">
      <div className="flex flex-col gap-1 mt-8" data-testid="mobile-menu">
        <MobileMenuGroup title="Roofing" items={NAV.roofing} onGo={onGo} />
        <div className="mt-3">
          <MobileMenuGroup title="Chimney" items={NAV.chimney} onGo={onGo} />
        </div>
        <div className="h-px bg-border my-3" />
        {SIMPLE_LINKS.map((l) => (
          <button key={l.label} onClick={() => onGo(l.path)} className={MOBILE_LINK_CLASS}>{l.label}</button>
        ))}
        <button onClick={() => onGo('/locations/new-jersey')} className={MOBILE_LINK_CLASS}>Locations</button>
        {TRAILING_LINKS.map((l) => (
          <button key={l.label} onClick={() => onGo(l.path)} className={MOBILE_LINK_CLASS}>{l.label}</button>
        ))}
        <a href={SITE.phoneHref} className="flex items-center gap-2 text-primary font-heading font-extrabold text-lg mt-4 px-2">
          <Phone className="w-5 h-5" /> {SITE.phone}
        </a>
        <button onClick={() => onGo('/free-estimate')} className="btn-cta mt-4">Free Estimate</button>
      </div>
    </SheetContent>
  </Sheet>
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const go = (path) => { setOpen(false); navigate(path); };

  return (
    <header className="sticky top-0 z-50" data-testid="site-header">
      <TopBar />
      <div className="bg-background/90 backdrop-blur-md border-b border-border">
        <div className="max-w-[1320px] mx-auto px-4 lg:px-8 flex items-center justify-between h-[76px]">
          <Link to="/" data-testid="header-logo" className="shrink-0">
            <img src={SITE.logoBlack} alt="Phoenix Roofing & Chimney" className="h-11 w-auto" />
          </Link>

          <DesktopNav />

          <div className="hidden lg:flex items-center gap-5">
            <a href={SITE.phoneHref} className="flex items-center gap-2.5 group" data-testid="header-phone">
              <span className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center transition-colors duration-200 group-hover:bg-primary">
                <Phone className="w-4 h-4 text-primary transition-colors duration-200 group-hover:text-primary-foreground" />
              </span>
              <span className="leading-tight">
                <span className="block text-[11px] uppercase tracking-wider font-heading font-bold text-muted-foreground">Call Edison</span>
                <span className="block font-heading font-extrabold text-[15px] text-foreground">{SITE.phone}</span>
              </span>
            </a>
            <Link to="/free-estimate" className="btn-cta !py-3 !px-5" data-testid="header-estimate-btn">Free Estimate</Link>
          </div>

          <MobileMenu open={open} onOpenChange={setOpen} onGo={go} />
        </div>
      </div>
    </header>
  );
};
